/**
 * Estrategia de fallback que utiliza directamente el token de Firebase 
 * como token de API cuando las otras estrategias OAuth2 fallan
 * Implementación modularizada siguiendo principios SOLID y patrones de alta cohesión
 */

import BaseTokenStrategy from './BaseTokenStrategy';

/**
 * Estrategia de último recurso que utiliza el token de Firebase directamente
 * como token de API con formato Bearer según RFC 6750, aplicable cuando las
 * demás estrategias de autenticación OAuth2 fallan
 */
class FirebaseTokenStrategy extends BaseTokenStrategy {
  /**
   * @param {Object} options - Opciones de configuración
   * @param {Object} [options.logger] - Logger para diagnóstico
   */
  constructor(options = {}) {
    super(options);
    this.logger = options.logger || console;
  }
  
  /**
   * Registra un error de forma estandarizada
   * @param {string} context - Contexto del error
   * @param {Error|string} error - Error ocurrido
   * @private
   */
  _logError(context, error) {
    this.logger.error(`[FirebaseTokenStrategy:${context}]`, error.message || error);
    try {
      localStorage.setItem('firebase_token_error', JSON.stringify({
        context,
        message: error.message || String(error),
        timestamp: new Date().toISOString()
      }));
    } catch (e) {/* Error no crítico */}
  }

  /**
   * Usa el token de Firebase directamente como mecanismo de fallback
   * cuando las estrategias OAuth2 estándar fallan
   * @param {Object} user - Usuario Firebase autenticado
   * @param {string} tokenToSend - Token Firebase para usar (puede incluir prefijo 'Bearer')
   * @param {string} tiendaId - ID de tienda (opcional)
   * @return {Promise<Object|null>} Token normalizado {token, format, expiresIn} o null
   */
  async execute(user, tokenToSend, tiendaId) {
    try {
      this.logger.info('[FirebaseTokenStrategy] Iniciando estrategia de fallback');
      
      // Validación inicial
      if (!tokenToSend) {
        this.logger.warn('[FirebaseTokenStrategy] No se proporcionó token Firebase');
        return null;
      }
      
      // Registrar información para diagnóstico
      try {
        localStorage.setItem('firebase_token_fallback_used', 'true');
        localStorage.setItem('firebase_token_fallback_timestamp', new Date().toISOString());
        localStorage.setItem('firebase_token_fallback_raw_length', String(tokenToSend.length));
        if (user?.uid) localStorage.setItem('firebase_token_user_id', user.uid);
        if (tiendaId) localStorage.setItem('firebase_token_tienda_id', tiendaId);
      } catch (e) {/* Error no crítico */}
      
      // Extraer el token puro sin el prefijo Bearer si está presente
      let pureToken = tokenToSend;
      if (tokenToSend.startsWith('Bearer ')) {
        this.logger.debug('[FirebaseTokenStrategy] Quitando prefijo Bearer del token');
        pureToken = tokenToSend.substring(7); // 'Bearer '.length = 7
      }
      
      // Verificar formato y estructura del token
      const tokenParts = pureToken.split('.');
      if (tokenParts.length !== 3) {
        this._logError('invalid_token_format', new Error(`El token Firebase no tiene formato JWT válido (partes=${tokenParts.length})`));
        try {
          localStorage.setItem('firebase_invalid_token_parts', String(tokenParts.length));
          localStorage.setItem('firebase_invalid_token_sample', pureToken.substring(0, 15) + '...');
        } catch (e) {/* Error no crítico */}
        return null;
      }
      
      try {
        // Extraer información del token (sin validar firma)
        const tokenPayload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
        
        // Verificar expiración del token
        const expirationTime = tokenPayload.exp * 1000; // Convertir a milisegundos
        const currentTime = Date.now();
        const remainingTimeMs = expirationTime - currentTime;
        
        if (remainingTimeMs <= 0) {
          this._logError('expired_token', new Error('El token Firebase ha expirado'));
          return null;
        }
        
        // Convertir a segundos para el formato OAuth2
        const expiresIn = Math.floor(remainingTimeMs / 1000);
        
        // Registrar metadatos del token para diagnóstico
        try {
          localStorage.setItem('firebase_token_metadata', JSON.stringify({
            subject: tokenPayload.sub,
            issuer: tokenPayload.iss,
            audience: tokenPayload.aud,
            expires_in: expiresIn,
            token_type: 'Bearer'
          }));
        } catch (e) {/* Error no crítico */}
        
        this.logger.info(`[FirebaseTokenStrategy] Utilizando token Firebase como fallback (expira en: ${expiresIn}s)`);
        
        // Retornar token normalizado
        return {
          token: tokenToSend,
          format: 'Bearer', // Siempre Bearer según RFC 6750
          expiresIn: expiresIn,
          source: 'firebase_fallback'
        };
      } catch (parseError) {
        // Error al procesar el token JWT
        this._logError('token_parse', parseError);
        
        // Aún así retornamos el token como último recurso
        this.logger.warn('[FirebaseTokenStrategy] Error al analizar token, usando como fallback de emergencia');
        
        return {
          token: tokenToSend,
          format: 'Bearer',
          expiresIn: 3600, // 1 hora por defecto cuando no podemos analizar
          source: 'firebase_emergency_fallback'
        };
      }
    } catch (error) {
      // Error general del método
      this._logError('execute', error);
      return null;
    }
  }
}

export default FirebaseTokenStrategy;
